import React, { useEffect, useRef } from "react";
import { animate } from "animejs";
import { createTimeline } from "animejs/timeline";
import { useDrawLoop } from "./utils";
import "./styles.css";

export const handler = ({ inputs, mechanic }) => {
  const {
    width,
    height,
    backgroundColor,
    primaryColor,
    secondaryColor,
    duration,
  } = inputs;

  const startTime = useRef(Date.now());
  const isPlaying = useRef(true);
  const frameCount = useDrawLoop(isPlaying.current);
  const timelineRef = useRef(null);
  const box1Ref = useRef(null);
  const box2Ref = useRef(null);
  const box3Ref = useRef(null);

  // Initialize anime.js v4 timeline once
  useEffect(() => {
    if (!timelineRef.current && box1Ref.current) {
      // Create anime.js v4 timeline with defaults
      const tl = createTimeline({
        defaults: {
          duration: duration * 1000,
          ease: "inOutQuad",
        },
        autoplay: false, // We control playback manually
      });

      // Add box 1 animation
      tl.add(
        box1Ref.current,
        {
          rotate: { to: 360 },
          scale: { to: [1, 1.5, 1] },
          translateY: { to: [-50, 50, 0] },
        },
        0,
      );

      // Add box 2 animation
      tl.add(
        box2Ref.current,
        {
          rotate: { to: -360 },
          scale: { to: [1, 0.5, 1] },
          translateX: { to: [-50, 50, 0] },
        },
        0,
      );

      // Add box 3 animation
      tl.add(
        box3Ref.current,
        {
          rotate: { to: 720 },
          scale: { to: [1, 2, 1] },
          borderRadius: { to: ["8px", "50%", "8px"] },
        },
        0,
      );

      timelineRef.current = tl;
    }
  }, [box1Ref.current, box2Ref.current, box3Ref.current, duration]);

  // Update animation progress and call mechanic.frame()
  useEffect(() => {
    if (timelineRef.current) {
      const elapsed = Date.now() - startTime.current;

      // Seek to the current time in the timeline
      timelineRef.current.seek(elapsed);

      if (elapsed < duration * 1000) {
        mechanic.frame();
      } else if (isPlaying.current) {
        isPlaying.current = false;
        mechanic.done();
      }
    }
  }, [frameCount, duration]);

  return (
    <div
      className="anime-container"
      style={{
        width: `${width}px`,
        height: `${height}px`,
        backgroundColor: backgroundColor,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "40px",
        position: "relative",
      }}
    >
      <div
        ref={box1Ref}
        className="anime-box"
        style={{
          width: "80px",
          height: "80px",
          backgroundColor: primaryColor,
          borderRadius: "8px",
        }}
      />

      <div
        ref={box2Ref}
        className="anime-box"
        style={{
          width: "80px",
          height: "80px",
          backgroundColor: secondaryColor,
          borderRadius: "8px",
        }}
      />

      <div
        ref={box3Ref}
        className="anime-box"
        style={{
          width: "80px",
          height: "80px",
          backgroundColor: primaryColor,
          borderRadius: "8px",
        }}
      />
    </div>
  );
};

export const inputs = {
  width: {
    type: "number",
    default: 600,
  },
  height: {
    type: "number",
    default: 400,
  },
  backgroundColor: {
    type: "color",
    model: "hex",
    default: "#1a1a1a",
  },
  primaryColor: {
    type: "color",
    model: "hex",
    default: "#6691cb",
  },
  secondaryColor: {
    type: "color",
    model: "hex",
    default: "#fcf3c2",
  },
  duration: {
    type: "number",
    default: 3,
    min: 1,
    max: 10,
    slider: true,
    label: "Duration (seconds)",
  },
};

export const settings = {
  engine: require("@mechanic-design/engine-react"),
  name: "Anime.js Example",
  animated: true,
  hideFunction: true,
};
